/**
 * 班级排课规则表（class-scheduling，班级详情抽屉「排课」页签）。
 *
 * 原型 ClassDetailDrawer 排课本签基础形态（星期/时间/教室只读表）+ 增补列
 * （状态/时长/教师/有效期/未来课次数）与「结束排课」操作（PRD §5.5 规则 5，
 * design OQ3 用户确认保留）：Popconfirm 展示影响数二次确认后取消未来课次。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { weekdayLabel } from "../../api/staff.api";
import {
  endSchedule,
  listSchedules,
  type ScheduleListItem,
} from "../../api/scheduling.api";
import { useHasPermission } from "../../auth/HasPermission";
import { ApiError } from "../../api/transport";

export function ScheduleRulesTable({ classId }: { classId: string }) {
  // 结束排课=ADM-19 EDIT（对齐后端 @Roles），无权限隐藏操作
  const canEndSchedule = useHasPermission("ADM-19", "EDIT");
  const [rules, setRules] = useState<ScheduleListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await listSchedules(classId));
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "排课规则加载失败");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleEnd(rule: ScheduleListItem) {
    try {
      const result = await endSchedule(rule.id, true);
      message.success(
        `已结束排课，取消未来 ${result.cancelledSessionCount} 个课次`,
      );
      await load();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "结束排课失败");
    }
  }

  const columns: ColumnsType<ScheduleListItem> = [
    {
      title: "星期",
      dataIndex: "dayOfWeek",
      width: 72,
      render: (v: number) => weekdayLabel[v],
    },
    { title: "开始时间", dataIndex: "startTime", width: 88 },
    {
      title: "时长",
      dataIndex: "durationMinutes",
      width: 80,
      render: (v: number) => `${v} 分钟`,
    },
    { title: "教室", dataIndex: "classroomName" },
    { title: "教师", dataIndex: "teacherName" },
    {
      title: "有效期",
      width: 200,
      render: (_, r) => `${r.effectiveFrom} ~ ${r.effectiveTo}`,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 88,
      render: (v: string) =>
        v === "ACTIVE" ? (
          <Tag color="processing">生效中</Tag>
        ) : (
          <Tag>已结束</Tag>
        ),
    },
    {
      title: "未来课次",
      dataIndex: "futureScheduledSessionCount",
      width: 96,
      render: (v: number) => v,
    },
    {
      title: "操作",
      fixed: "right",
      width: 110,
      render: (_, r) =>
        canEndSchedule && r.status === "ACTIVE" ? (
          <Popconfirm
            title={`确定结束该排课？将取消未来 ${r.futureScheduledSessionCount} 个课次`}
            okText="确认结束"
            cancelText="取消"
            onConfirm={() => void handleEnd(r)}
          >
            <Button size="small" danger>
              结束排课
            </Button>
          </Popconfirm>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  if (rules.length === 0 && !loading) {
    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          暂无排课规则，可在排课工作台为该班级排课。
        </Typography.Text>
      </Space>
    );
  }

  return (
    <Table
      scroll={{ x: "max-content" }}
      rowKey="id"
      dataSource={rules}
      columns={columns}
      pagination={false}
      size="small"
      loading={loading}
    />
  );
}
